import { useState, useEffect, useCallback } from 'react'
import { Card } from '@/components/Card'
import { fetchApi } from '@/lib/api'
import { Search, Clock, Copy, Check, ChevronDown, ChevronRight } from 'lucide-react'
import { formatDateTime } from '@/lib/date'

type LogEvent = {
  timestamp: number
  message: string
  logStreamName: string
}

type LogGroup = {
  name: string
  storedBytes: number
  retentionInDays: number | null
}

type TimeRange = {
  label: string
  minutes: number
}

const TIME_RANGES: TimeRange[] = [
  { label: '15m', minutes: 15 },
  { label: '1h', minutes: 60 },
  { label: '6h', minutes: 360 },
  { label: '24h', minutes: 1440 },
  { label: '3d', minutes: 4320 },
  { label: '7d', minutes: 10080 },
]

export function Logs() {
  const [env, setEnv] = useState<'prod' | 'dev'>('prod')
  const [logGroups, setLogGroups] = useState<LogGroup[]>([])
  const [logGroup, setLogGroup] = useState('')
  const [filterPattern, setFilterPattern] = useState('ERROR')
  const [selectedRange, setSelectedRange] = useState<TimeRange>(TIME_RANGES[1])
  const [events, setEvents] = useState<LogEvent[]>([])
  const [loading, setLoading] = useState(false)
  const [loadingGroups, setLoadingGroups] = useState(false)
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set())
  const [copiedIdx, setCopiedIdx] = useState<number | null>(null)

  const fetchLogGroups = useCallback(async (environment: string) => {
    setLoadingGroups(true)
    try {
      const data = await fetchApi<{ log_groups: LogGroup[] }>(`/logs/groups/${environment}`)
      setLogGroups(data.log_groups)
      if (data.log_groups.length > 0) {
        setLogGroup(data.log_groups[0].name)
      }
    } catch {
      setLogGroups([])
    } finally {
      setLoadingGroups(false)
    }
  }, [])

  useEffect(() => {
    fetchLogGroups(env)
  }, [env, fetchLogGroups])

  const search = async () => {
    if (!logGroup) return
    setLoading(true)
    setExpandedRows(new Set())
    try {
      const now = Date.now()
      const startTime = now - selectedRange.minutes * 60 * 1000
      const params = new URLSearchParams({
        log_group: logGroup,
        filter_pattern: filterPattern,
        start_time: String(startTime),
        end_time: String(now),
        limit: '200',
      })
      const data = await fetchApi<{ events: LogEvent[] }>(`/logs/search/${env}?${params}`)
      setEvents(data.events)
    } catch {
      setEvents([])
    } finally {
      setLoading(false)
    }
  }

  const toggleRow = (idx: number) => {
    setExpandedRows((prev) => {
      const next = new Set(prev)
      if (next.has(idx)) next.delete(idx)
      else next.add(idx)
      return next
    })
  }

  const copyMessage = (text: string, idx: number) => {
    navigator.clipboard.writeText(text)
    setCopiedIdx(idx)
    setTimeout(() => setCopiedIdx(null), 2000)
  }

  const isJsonMessage = (msg: string) => {
    const trimmed = msg.trim()
    return trimmed.startsWith('{') || trimmed.startsWith('[')
  }

  const formatMessage = (msg: string) => {
    if (isJsonMessage(msg)) {
      try {
        return JSON.stringify(JSON.parse(msg), null, 2)
      } catch {
        return msg
      }
    }
    return msg
  }

  const getMessageLevel = (msg: string): string | null => {
    const lower = msg.toLowerCase()
    if (lower.includes('"level":"error"') || lower.includes('error') || lower.includes('fatal')) return 'error'
    if (lower.includes('"level":"warn"') || lower.includes('warning') || lower.includes('warn')) return 'warn'
    return null
  }

  return (
    <div className="p-6 space-y-4">
      <h2 className="text-xl font-bold">Log Search</h2>

      {/* Search Controls */}
      <Card>
        <div className="space-y-3">
          {/* Row 1: Env + Log Group */}
          <div className="flex gap-2">
            <div className="flex rounded overflow-hidden border border-[var(--color-border)]">
              {(['prod', 'dev'] as const).map((e) => (
                <button
                  key={e}
                  onClick={() => setEnv(e)}
                  className={`px-3 py-1 text-sm ${
                    env === e
                      ? 'bg-[var(--color-primary)] text-white'
                      : 'bg-[var(--color-bg)] text-[var(--color-text-muted)]'
                  }`}
                >
                  {e.toUpperCase()}
                </button>
              ))}
            </div>
            <select
              value={logGroup}
              onChange={(e) => setLogGroup(e.target.value)}
              disabled={loadingGroups}
              className="flex-1 px-3 py-1 text-sm rounded bg-[var(--color-bg)] border border-[var(--color-border)] text-[var(--color-text)]"
            >
              {loadingGroups ? (
                <option>Loading...</option>
              ) : logGroups.length === 0 ? (
                <option>No log groups found</option>
              ) : (
                logGroups.map((g) => (
                  <option key={g.name} value={g.name}>{g.name}</option>
                ))
              )}
            </select>
          </div>

          {/* Row 2: Time Range + Filter + Search */}
          <div className="flex gap-2 items-center">
            <div className="flex items-center gap-1">
              <Clock size={14} className="text-[var(--color-text-muted)]" />
              <div className="flex rounded overflow-hidden border border-[var(--color-border)]">
                {TIME_RANGES.map((range) => (
                  <button
                    key={range.label}
                    onClick={() => setSelectedRange(range)}
                    className={`px-2 py-1 text-xs ${
                      selectedRange.label === range.label
                        ? 'bg-[var(--color-primary)] text-white'
                        : 'bg-[var(--color-bg)] text-[var(--color-text-muted)] hover:bg-[var(--color-bg-elevated)]'
                    }`}
                  >
                    {range.label}
                  </button>
                ))}
              </div>
            </div>
            <input
              type="text"
              value={filterPattern}
              onChange={(e) => setFilterPattern(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && search()}
              placeholder='Filter pattern (e.g. ERROR, 500, { $.level = "error" })'
              className="flex-1 px-3 py-1.5 text-sm rounded bg-[var(--color-bg)] border border-[var(--color-border)] text-[var(--color-text)] placeholder-[var(--color-text-muted)]"
            />
            <button
              onClick={search}
              disabled={loading || !logGroup}
              className="flex items-center gap-2 px-4 py-1.5 text-sm rounded bg-[var(--color-primary)] text-white hover:opacity-90 disabled:opacity-50"
            >
              <Search size={14} />
              Search
            </button>
          </div>
        </div>
      </Card>

      {/* Results */}
      <Card title={`Results (${events.length})`}>
        {loading ? (
          <p className="text-sm text-[var(--color-text-muted)]">Searching...</p>
        ) : events.length === 0 ? (
          <p className="text-sm text-[var(--color-text-muted)]">No results. Run a search above.</p>
        ) : (
          <div className="space-y-1 max-h-[70vh] overflow-auto">
            {events.map((event, i) => {
              const expanded = expandedRows.has(i)
              const isLong = event.message.length > 200
              const level = getMessageLevel(event.message)
              const borderColor = level === 'error'
                ? 'border-l-red-500'
                : level === 'warn'
                ? 'border-l-yellow-500'
                : 'border-l-transparent'

              return (
                <div
                  key={i}
                  className={`p-2 rounded bg-[var(--color-bg)] font-mono text-xs border-l-2 ${borderColor}`}
                >
                  <div className="flex items-center gap-2">
                    {isLong && (
                      <button onClick={() => toggleRow(i)} className="text-[var(--color-text-muted)] hover:text-[var(--color-text)]">
                        {expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                      </button>
                    )}
                    <span className="text-[var(--color-text-muted)] shrink-0">
                      {formatDateTime(event.timestamp)}
                    </span>
                    <span className="text-[var(--color-text-muted)] truncate text-[10px]">
                      {event.logStreamName.split('/').pop()}
                    </span>
                    <button
                      onClick={() => copyMessage(event.message, i)}
                      className="ml-auto text-[var(--color-text-muted)] hover:text-[var(--color-text)] shrink-0"
                      title="Copy message"
                    >
                      {copiedIdx === i ? <Check size={12} className="text-green-500" /> : <Copy size={12} />}
                    </button>
                  </div>
                  <pre className={`mt-1 whitespace-pre-wrap break-all text-[var(--color-text)] ${
                    !expanded && isLong ? 'max-h-16 overflow-hidden' : ''
                  }`}>
                    {expanded ? formatMessage(event.message) : event.message}
                  </pre>
                  {!expanded && isLong && (
                    <button
                      onClick={() => toggleRow(i)}
                      className="text-[10px] text-[var(--color-primary)] hover:underline mt-1"
                    >
                      Show more...
                    </button>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </Card>
    </div>
  )
}
